import { parseBackup, planMerge, selectFromEnvelope, summarizeEnvelope } from "@app/core";
import { create } from "zustand";
import { getServices } from "../platform.js";
import { openBackupFile, saveBackupFile } from "../lib/backup-file.js";
import { clearSelectedCategories, gatherSnapshot, insertAll, rehydrateStores } from "../lib/snapshot.js";
import { reconcileDuplicateEntities } from "../lib/dedupe-entities.js";
import { gatherPreferences } from "../lib/preferences-record.js";
import { tr } from "../lib/tr.js";
import { useBackupConfirmStore } from "./backupConfirm.store.js";
import { useBackupImportStore } from "./backupImport.store.js";
import { useSettingsStore } from "./settings.store.js";
import { useThemeStore } from "./theme.store.js";
import { toast } from "./toast.store.js";

/** What a running import/restore is doing, for the Data settings card. Only
 *  meaningful because the write paths yield between batches — a single blocking
 *  call could not paint this. */
export interface BackupProgress {
  phase: "clearing" | "writing";
  percent: number;
}

interface BackupState {
  busy: boolean;
  error: string | null;
  progress: BackupProgress | null;
  exportData: () => Promise<void>;
  importData: () => Promise<void>;
  deleteAll: () => Promise<void>;
}

export const useBackupStore = create<BackupState>((set) => ({
  busy: false,
  error: null,
  progress: null,

  async exportData() {
    set({ busy: true, error: null });
    try {
      const envelope = { ...(await gatherSnapshot()), preferences: gatherPreferences() };
      const today = new Date().toISOString().slice(0, 10);
      await saveBackupFile(JSON.stringify(envelope, null, 2), `reminders-backup-${today}.json`);
      toast.success(tr("toast.backup.saved"));
    } catch (e) {
      toast.error(tr("toast.backup.failed", { error: errMessage(e) }));
    } finally {
      set({ busy: false });
    }
  },

  async importData() {
    set({ error: null });
    const text = await openBackupFile();
    if (text === null) return; // cancelled

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      set({ error: tr("toast.file.invalidJson") });
      toast.error(tr("toast.file.invalidJson"));
      return;
    }
    const result = parseBackup(parsed);
    if (!result.ok) {
      set({ error: tr("toast.backup.notValid", { error: result.error }) });
      toast.error(tr("toast.backup.notValid", { error: result.error }));
      return;
    }

    const res = await useBackupImportStore.getState().ask(summarizeEnvelope(result.envelope));
    if (res.kind === "cancel") return;

    // Narrow the envelope to the categories the user checked.
    const envelope = selectFromEnvelope(result.envelope, res.selection);

    set({ busy: true, progress: null });
    const report = (phase: BackupProgress["phase"]) => (done: number, total: number) => {
      const percent = total === 0 ? 100 : Math.round((done / total) * 100);
      const cur = useBackupStore.getState().progress;
      if (cur?.phase !== phase || cur.percent !== percent) set({ progress: { phase, percent } });
    };
    try {
      if (res.mode === "replace") {
        // Replace ONLY the checked categories — unchecked data is left intact.
        await clearSelectedCategories(res.selection, report("clearing"));
        await insertAll(envelope, report("writing"));
      } else {
        await insertAll(planMerge({ existing: await existingIds(), incoming: envelope }), report("writing"));
      }
      // Safety net: collapse any same-name duplicates the import may have left.
      await reconcileDuplicateEntities();
      await rehydrateStores();
      // Preferences ride along only when "Settings" was checked (selectFromEnvelope
      // strips them otherwise).
      if (envelope.preferences) {
        await useSettingsStore.getState().applyBackupPreferences(envelope.preferences);
      }
      if (envelope.themes?.[0]) {
        await useThemeStore.getState().applyRemoteTheme(envelope.themes[0]);
      }
      toast.success(tr(res.mode === "replace" ? "toast.backup.restored" : "toast.backup.merged"));
    } catch (e) {
      toast.error(tr("toast.backup.restoreFailed", { error: errMessage(e) }));
    } finally {
      set({ busy: false, progress: null });
    }
  },

  async deleteAll() {
    const choice = await useBackupConfirmStore.getState().ask({ kind: "delete" });
    if (choice !== "confirm") return;
    set({ busy: true, error: null });
    try {
      await getServices().backup.wipeData();
      await rehydrateStores();
      toast.success(tr("toast.data.deleted"));
    } catch (e) {
      toast.error(tr("toast.data.deleteFailed", { error: errMessage(e) }));
    } finally {
      set({ busy: false });
    }
  },
}));

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

async function existingIds() {
  const e = await gatherSnapshot();
  return {
    taskIds: new Set(e.tasks.map((t) => t.id)),
    ruleIds: new Set(e.recurrenceRules.map((r) => r.id)),
    overrideIds: new Set(e.recurrenceOverrides.map((o) => o.id)),
    // Full rows — labels/topics/calendars are deduped by NAME, not just id.
    labels: e.labels,
    topics: e.topics,
    calendars: e.calendars,
  };
}
